import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import Button from '../UI/Button';

interface Props {
  isOpen: boolean;
  dietId: string;
  onClose: () => void;
  onSave: (selectedRecipes: Record<string, Record<string, string>>) => void; // { mealType: { day: recipeId } }
  initialSelections?: Record<string, Record<string, string>>;
}

const MEAL_OPTIONS: string[] = [
  'Breakfast',
  'Snack AM',
  'Lunch',
  'Side Dish Lunch',
  'Snack PM',
  'Dinner',
  'Side Dish Dinner',
];

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const RecipeSelectionModal: React.FC<Props> = ({ isOpen, dietId, onClose, onSave, initialSelections = {} }) => {
  const [selectedRecipes, setSelectedRecipes] = useState<Record<string, Record<string, string>>>(initialSelections);
  const [currentWeek, setCurrentWeek] = useState(1);

  const { data: menusRes } = useQuery({
    queryKey: ['menus', 'by-diet', dietId],
    queryFn: () => dietService.getMenus({ dietId, limit: 1000 }),
    enabled: !!dietId && isOpen,
  });

  const menus = menusRes?.menus || [];
  const menu = menus[0]; // assume a single menu per diet

  const [schedule, setSchedule] = useState<Record<string, any[]>>({});
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  useEffect(() => {
    if (!menu?.id || !isOpen) return;

    async function loadSchedule() {
      setLoadingSchedule(true);
      try {
        console.log('[RecipeSelectionModal] Loading schedule for menu:', menu.id);
        const avail = await dietService.getAvailableMenuDayPlates(menu.id);
        const combos: Array<{ week: number | null; mealType: string }> = (avail as any)?.items || [];
        console.log('[RecipeSelectionModal] Available combos:', combos);

        const scheduleData: Record<string, any[]> = {};

        for (const combo of combos) {
          const week = combo.week === null ? 'all' : String(combo.week);
          const mealType = combo.mealType;
          const key = `${week}__${mealType}`; // Use double underscore like MenuDetailsViewPage

          console.log(`[RecipeSelectionModal] Loading plates for week=${week}, meal=${mealType}`);
          const plates = await dietService.getMenuDayPlates(menu.id, {
            week: combo.week === null ? 'all' : combo.week,
            meal: combo.mealType,
          });

          const items = (plates as any)?.items || [];
          console.log(`[RecipeSelectionModal] Got ${items.length} items for ${key}:`, items);
          scheduleData[key] = items;
        }

        console.log('[RecipeSelectionModal] Final schedule data:', scheduleData);
        setSchedule(scheduleData);
      } catch (error) {
        console.error('[RecipeSelectionModal] Error loading schedule:', error);
      } finally {
        setLoadingSchedule(false);
      }
    }

    loadSchedule();
  }, [menu?.id, isOpen]);

  const handleRecipeSelect = (mealType: string, day: string, recipeId: string) => {
    setSelectedRecipes(prev => ({
      ...prev,
      [mealType]: {
        ...(prev[mealType] || {}),
        [day]: recipeId,
      },
    }));
  };

  const handleSave = () => {
    onSave(selectedRecipes);
    onClose();
  };

  const getRecipeForCell = (mealType: string, dayIndex: number) => {
    const key = `${currentWeek}__${mealType}`; // Use double underscore
    const plates = schedule[key] || [];

    console.log(`[RecipeSelectionModal] getRecipeForCell - key=${key}, dayIndex=${dayIndex}, plates:`, plates);

    // Find plate for this day (dayIndex is 0-based, but day in data is 1-based)
    const plate = plates.find((p: any) => Number(p.day) === dayIndex + 1);
    console.log(`[RecipeSelectionModal] Found plate for day ${dayIndex + 1}:`, plate);

    // The API returns raw SQL results with fields like: profile_name, plate_name, recipe_name, recipePlateId
    if (plate) {
      // Create a recipe-like object from the available data
      const recipeName = plate.profile_name || plate.plate_name || plate.recipe_name || '';
      const recipeId = plate.recipePlateId || plate.id || '';

      if (recipeName && recipeId) {
        const recipe = {
          id: recipeId,
          name: recipeName,
        };
        console.log(`[RecipeSelectionModal] Extracted recipe:`, recipe);
        return recipe;
      }
    }
    return null;
  };

  const isRecipeSelected = (mealType: string, day: string, recipeId: string) => {
    return selectedRecipes[mealType]?.[day] === recipeId;
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={() => {}}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-[95vw] h-[90vh] transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col">
                <div className="flex items-center justify-between border-b px-6 py-4 flex-shrink-0">
                  <Dialog.Title className="text-lg font-semibold">Select Recipes for Client</Dialog.Title>
                  <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                  {/* Week selector */}
                  <div className="mb-4 flex items-center gap-2">
                    <label className="font-medium">Week:</label>
                    <select
                      className="form-input"
                      value={currentWeek}
                      onChange={(e) => setCurrentWeek(Number(e.target.value))}
                    >
                      {[1, 2, 3, 4].map(w => (
                        <option key={w} value={w}>Week {w}</option>
                      ))}
                    </select>
                  </div>

                  {loadingSchedule ? (
                    <div className="text-center py-8">Loading menu...</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-2 border-gray-400">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border-2 border-gray-400 px-3 py-2 text-left font-semibold">Meal</th>
                            {DAY_LABELS.map(day => (
                              <th key={day} className="border-2 border-gray-400 px-3 py-2 text-center font-semibold">
                                {day}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {MEAL_OPTIONS.map(mealType => (
                            <tr key={mealType}>
                              <td className="border-2 border-gray-400 px-3 py-2 font-medium bg-gray-50">
                                {mealType}
                              </td>
                              {DAY_LABELS.map((day, dayIndex) => {
                                const recipe = getRecipeForCell(mealType, dayIndex);
                                const isSelected = recipe ? isRecipeSelected(mealType, day, recipe.id) : false;
                                
                                return (
                                  <td 
                                    key={day} 
                                    className={`border-2 border-gray-400 px-2 py-2 text-center cursor-pointer hover:bg-blue-50 ${
                                      isSelected ? 'bg-blue-100' : ''
                                    }`}
                                    onClick={() => {
                                      if (recipe) {
                                        handleRecipeSelect(mealType, day, recipe.id);
                                      }
                                    }}
                                  >
                                    {recipe ? (
                                      <div className="text-sm">
                                        <div className="font-medium">{recipe.name}</div>
                                        {isSelected && (
                                          <div className="text-xs text-blue-600 mt-1">✓ Selected</div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-xs">No recipe</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mt-4 text-sm text-gray-600">
                    <p>Click on a recipe to select/deselect it for this client's cycle.</p>
                    <p className="mt-1">Selected recipes: {Object.values(selectedRecipes).reduce((sum, meals) => sum + Object.keys(meals).length, 0)}</p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t px-6 py-4 flex-shrink-0">
                  <Button type="button" variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleSave}>
                    Save Selection
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RecipeSelectionModal;

